/* eslint-disable camelcase */

import React from 'react';
import PropTypes from 'prop-types';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { hasRegions, getRegionFromRegionName } from 'cirrus/Dropdown/Region/utils';
import countries from 'cirrus/Dropdown/Country/country.list.json';
import PlacesSuggestions from './PlacesSuggestions';

const Autocomplete = ({ values, setValues, debounce, children }) => {
  const handlePlacesChange = newAddress => {
    setValues({ ...values, address1: newAddress });
  };

  const handlePlacesSelect = inputAddress => {
    geocodeByAddress(inputAddress)
      .then(results => {
        const formattedResults = {};
        const newAddress = [];

        if (!results || !results.length) {
          return;
        }

        const { address_components: addressComponents = [] } = results[0];

        addressComponents.forEach(component => {
          const { long_name: longName, short_name: shortName } = component;
          component.types.forEach(componentType => {
            switch (componentType) {
              case 'street_number':
              case 'street_address':
              case 'route':
                newAddress.push(longName);
                break;
              case 'administrative_area_level_1':
              case 'administrative_area_level_2':
              case 'country':
              case 'locality':
              case 'postal_code':
                formattedResults[componentType] = {
                  longName,
                  shortName,
                };
                break;
              default:
                // does nothing
                break;
            }
          });
        });

        const countryCode = formattedResults?.country?.shortName?.toUpperCase() || '';
        const cityFallback = formattedResults?.administrative_area_level_2?.longName || '';
        const city = formattedResults?.locality?.longName || cityFallback;

        // fetch country name from the country list
        const { name: countryName = '' } = countryCode ? countries[countryCode] : {};

        // fetch region code and name from Google (default) or from the region list
        let { shortName: regionCode = '', longName: regionName = '' } =
          formattedResults?.administrative_area_level_1 || {};

        if (countryCode && hasRegions(countryCode)) {
          ({ regionCode, regionName } = getRegionFromRegionName({
            countryCode,
            regionName: regionCode || regionName,
          }));
        }

        getLatLng(results[0]).then(({ lat, lng }) => {
          const newValues = {
            address1: newAddress.join(' '),
            city,
            countryCode,
            countryName,
            lat,
            lng,
            regionCode,
            regionName,
            zipcode: formattedResults?.postal_code?.longName || '',
          };

          setValues(
            {
              ...values,
              ...newValues,
            },
            true // TODO: doesn't seem to validate after setting values
          );
        });
      })
      .catch(() => {
        // TODO: log errors from Google Places or fail silently?
      });
  };

  // Prevents app from breaking if Google isn't available
  if (!window.google || !window.google.maps.places) {
    return children;
  }

  return (
    <PlacesAutocomplete
      value={values.address1}
      debounce={debounce}
      onChange={handlePlacesChange}
      onSelect={handlePlacesSelect}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div>
          {React.cloneElement(children, { ...getInputProps() })}
          <PlacesSuggestions
            loading={loading}
            suggestions={suggestions}
            getSuggestionItemProps={getSuggestionItemProps}
          />
        </div>
      )}
    </PlacesAutocomplete>
  );
};

Autocomplete.defaultProps = {
  debounce: 750,
};

Autocomplete.propTypes = {
  children: PropTypes.node,
  debounce: PropTypes.number,
  values: PropTypes.shape({
    address1: PropTypes.string,
    city: PropTypes.string,
    countryCode: PropTypes.string,
    countryName: PropTypes.string,
    lat: PropTypes.number,
    lng: PropTypes.number,
    regionCode: PropTypes.string,
    regionName: PropTypes.string,
    zipcode: PropTypes.string,
  }),
  setValues: PropTypes.func,
};

export default Autocomplete;
